import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse, baseApiUrl, fetcher } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import { PolicyMetricsResponse } from '../_types';
import { generatePolicyMetrics } from './riskPoolOrgDataGenerator';
import { useRiskPoolDemoData } from '../../../../_data_sources/useRiskPoolDemoData';

interface QueryParams {
  startDate: string;
  endDate: string;
  manualId?: string;
}

interface ApiPolicyMetricsResponse {
  acknowledgments_completed: {
    count: number;
    percent: number;
    status: 'GOOD' | 'CONCERNING' | 'CRITICAL';
    totalCount: number;
  };
  dtbs_taken: {
    count: number;
    percent: number;
    status: 'GOOD' | 'CONCERNING' | 'CRITICAL';
    totalCount: number;
  };
  lexipol_updates: {
    count: number;
    percent: number;
    status: 'GOOD' | 'CONCERNING' | 'CRITICAL';
    totalCount: number;
  };
  policies_published: {
    count: number;
    percent: number;
    status: 'GOOD' | 'CONCERNING' | 'CRITICAL';
    totalCount: number;
  };
  dtbs_issued: {
    count: number;
    percent: number;
    status: 'GOOD' | 'CONCERNING' | 'CRITICAL';
    totalCount: number;
  };
}

export const getPolicyMetrics = async (
  parentOrgId: string,
  childOrgId: string,
  queryParams: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<PolicyMetricsResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${parentOrgId}/risk-pool/related-organizations/${childOrgId}/policy/metrics`;

  const params = new URLSearchParams();
  if (queryParams.startDate) params.append('startDate', queryParams.startDate);
  if (queryParams.endDate) params.append('endDate', queryParams.endDate);

  const queryString = params.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const apiData: ApiPolicyMetricsResponse = await fetcher({
      method: 'get',
      url: fullUrl,
      options: { signal },
    });

    // Transform API response to match expected type structure
    // For policies_published: API has count/percent/totalCount, we need total/percentChange
    // For dtbs_issued: API has count/percent/totalCount, we need agencyIssuedCount/lexipolIssuedCount
    // Note: The API doesn't provide percentChange or separate agency/lexipol counts,
    // so we'll use the count as total and set percentChange to 0, and use count/totalCount for dtbs_issued
    const transformed: PolicyMetricsResponse = {
      acknowledgments_completed: {
        percent: apiData.acknowledgments_completed.percent,
        count: apiData.acknowledgments_completed.count,
        totalCount: apiData.acknowledgments_completed.totalCount,
        status: apiData.acknowledgments_completed.status,
      },
      dtbs_taken: {
        percent: apiData.dtbs_taken.percent,
        count: apiData.dtbs_taken.count,
        totalCount: apiData.dtbs_taken.totalCount,
        status: apiData.dtbs_taken.status,
      },
      lexipol_updates: {
        percent: apiData.lexipol_updates.percent,
        count: apiData.lexipol_updates.count,
        totalCount: apiData.lexipol_updates.totalCount,
        status: apiData.lexipol_updates.status,
      },
      policies_published: {
        total: apiData.policies_published.count,
        percentChange: 0, // API doesn't provide percentChange, defaulting to 0
        status: apiData.policies_published.status,
      },
      dtbs_issued: {
        percent: apiData.dtbs_issued.percent,
        agencyIssuedCount: apiData.dtbs_issued.count,
        lexipolIssuedCount: apiData.dtbs_issued.totalCount,
        status: apiData.dtbs_issued.status,
      },
    };

    return transformed;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function usePolicyMetrics(
  childOrgId: string,
  queryParams: QueryParams,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId; // Fallback to childOrgId if not available
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<PolicyMetricsResponse, Error>(
    [
      'risk_pool_org_policy_metrics',
      parentOrgId,
      childOrgId,
      queryParams.startDate,
      queryParams.endDate,
      queryParams.manualId,
    ],
    async ({ signal }): Promise<PolicyMetricsResponse> => {
      try {
        if (isDemoDataEnabled) {
          return generatePolicyMetrics(childOrgId, queryParams.startDate, queryParams.endDate);
        }

        return await getPolicyMetrics(parentOrgId, childOrgId, queryParams, signal, onError);
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!queryParams.startDate && !!queryParams.endDate,
    }
  );
}
